import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Button, ButtonGroup } from 'reactstrap';
import CurrencySelect from './CurrencySelect';
import NumberInput from './NumberInput';
import styles from './PackageSummary.scss';
import LockIcon from './../assets/images/Lock_icon.png';
import LoadingIndicator from './LoadingIndicator';
import capitalize from 'lodash/capitalize';

export default class PackageSummary extends Component {

  static propTypes = {
    currency: PropTypes.string,
    currentPrice: PropTypes.number,
    packageType: PropTypes.string,
    packageVolume: PropTypes.string,
    eventQuantity: PropTypes.number,
    onQuantityChange: PropTypes.func.isRequired,
    updatePrintEligible: PropTypes.func.isRequired,
    submitButtonText: PropTypes.string.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCurrencyChange: PropTypes.func.isRequired,
    onTypeChange: PropTypes.func.isRequired,
    changeIsUpgrading: PropTypes.func,
    isLoading: PropTypes.bool,
    isLoadingPrice: PropTypes.bool,
    isValidForOriginPublisher: PropTypes.bool,
    snapSell: PropTypes.bool,
    packageTemplateSet: PropTypes.object.isRequired,
    availableCurrencies: PropTypes.object.isRequired,
    publishersCount: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]),
    isUpgrading: PropTypes.bool.isRequired,
    creditsLeft: PropTypes.number,
    invoice_id: PropTypes.number,
    email_campaign_id: PropTypes.number,
    is_email_campaign_enterprise: PropTypes.bool,
    email_campaign_audience:  PropTypes.string,
    email_campaign_location:  PropTypes.string,
    email_campaign_broadcast_time: PropTypes.string,
    publisher_id: PropTypes.number,
    market: PropTypes.string.isRequired,
    isTicketed: PropTypes.bool,
    isPrepay: PropTypes.bool
  };

  static defaultProps = {
    invoice_id: null,
    email_campaign_id: null,
    publisher_id: null
  }

  handleChangeType = (type) => {
    if (type === 'free') {
      this.props.changeIsUpgrading(false);
    }
    this.props.onTypeChange(type);
  }

  handleCurrencyChange = (newCurrency) => {
    this.props.onCurrencyChange(newCurrency);
  }

  handleUseCredits = () => {
    this.props.changeIsUpgrading(true);
    this.props.onTypeChange('single');
  }

  currentPackagePerks() {
    const plan = `${capitalize(this.props.market)} Plan`;
    const { publishersCount, packageVolume, partnerExports, featuringPublisher, isValidForOriginPublisher } = this.props;
    const sites = (packageVolume) ? `${packageVolume} Listing Sites` :
      `${publishersCount || '...'} Listing Site${publishersCount > 1 || publishersCount === 'Unlimited' ? 's' : ''}`;

    let mainPaidPerks = ['Featured Position'];
    let freePerks = [sites, 'Social Reposting', this.props.isTicketed && 'Ticketing',
      'Basic Reporting, Upgrade Later.', 'Promotion Till The Event Ends'].filter(Boolean);
    let paidPerks = ['Social Reposting', this.props.isTicketed && 'Ticketing',
      'Analytical Reporting', 'Unlimited Edits', 'Promotion Till The Event Ends',
      'Premium Support'].filter(Boolean);

    const features = (partnerExports && isValidForOriginPublisher) ? partnerExports.toJS() : {};
    const featuringPublisherAttributes = featuringPublisher ? featuringPublisher.toJS() : {};

    if (features.voice) paidPerks.unshift('Voice Search');
    if (features.print) paidPerks.unshift('Print Inclusion');
    if (featuringPublisherAttributes.radio_enabled) mainPaidPerks.unshift('Radio');

    const perksEmailCampaign = [
      this.props.email_campaign_audience,
      this.props.is_email_campaign_enterprise ? 'Targeted Recipients on Interest & Occupation' : 'Targeted Recipients on Proximity',
      this.props.email_campaign_location,
      'Tracked Open & Click Rates',
      'Optimized for Mobile & Desktop',
      `Sent at ${this.props.email_campaign_broadcast_time}`,
      'List is Audited & Verified Every 12 months',
      'Premium Support'
    ];

    const perks = {
      'free': freePerks,
      'single': [...mainPaidPerks, sites, ...paidPerks],
      'subscription': [...mainPaidPerks, sites, ...paidPerks],
      'email_campaign' : perksEmailCampaign,
      'publisher': ['50% Net Revenue Share', 'Publishing Solutions', 'Editor CMS', 'Sales CRM'],
      'free_publisher': ['Manage until you\'re ready to publish']
    };

    return perks[this.props.packageType];
  }

  currentPackageCons() {
    return {
      'free': ['Standard Reporting', 'Online Support'],
      'single': [],
      'subscription': [],
      'email_campaign' : [],
      'publisher': [],
      'free_publisher': ['Subscription required to publish']
    }[this.props.packageType];
  }

  shouldDisplayOption = (type) => {
    const { packageTemplateSet, snapSell, packageType, creditsLeft } = this.props;
    
    if (type === 'free' && this.props.publisher_id !== null) {
      return !this.props.isUpgrading;
    }

    if (type !== 'free' && creditsLeft > 0)
      return false;
    
    return packageTemplateSet.get(`${type}_available`) && !snapSell ||
      snapSell && packageType === type;
  };

  promoterPackageOptions = () => {
    const { isUpgrading, creditsLeft, packageType } = this.props;
    const hasCreditsLeft = creditsLeft > 0;

    return (
      <Fragment>
        { this.shouldDisplayPackages() && <ButtonGroup>
          {this.shouldDisplayOption('single') && <Button
            outline={packageType !== 'single'}
            onClick={() => this.handleChangeType('single')}
            color={packageType === 'single' ? 'primary' : 'light'}
          >Premium</Button>}
          { hasCreditsLeft && <Button
            outline={!isUpgrading}
            onClick={() => this.handleUseCredits() }
            color={isUpgrading ? 'primary' : 'light'}>Use Credit </Button>}
          {this.shouldDisplayOption('free') && <Button
            outline={!this.isSelectedFree()}
            onClick={() => this.handleChangeType('free')}
            color={this.isSelectedFree() ? 'primary' : 'light'}>Free</Button>}
        </ButtonGroup> }
      </Fragment>
    );
  }

  partnerPackageOptions(){
    const { packageType} = this.props;

    return (
      <ButtonGroup>
        <Button
          outline={packageType !== 'publisher'}
          onClick={() => this.handleChangeType('publisher')}
          color={!this.isSelectedFree() ? 'primary' : 'light'}
        >Subscription</Button>
        {this.shouldDisplayOption('free') && <Button
          outline={packageType !== 'free_publisher'}
          onClick={() => this.handleChangeType('free_publisher')}
          color={this.isSelectedFree() ? 'primary' : 'light'}
        >Free</Button>}
      </ButtonGroup>
    );
  }
  
  shouldDisplayPackages = () => {
    return (this.props.invoice_id === null && this.props.email_campaign_id === null && this.props.publisher_id === null);
  }
  
  isSelectedFree = () => {
    const { packageType, isUpgrading } = this.props;
    return (!isUpgrading && packageType === 'free' || packageType === 'free_publisher');
  }

  render() {
    const {
      currency,
      currentPrice,
      packageType,
      submitButtonText,
      onSubmit,
      isLoading,
      isLoadingPrice,
      eventQuantity,
      onQuantityChange,
      availableCurrencies,
      isUpgrading,
      creditsLeft,
      email_campaign_id,
      publisher_id,
      isPrepay,
      featuringPublisher,
      printEligible,
      updatePrintEligible
    } = this.props;

    const usesExistingPackage = isUpgrading && creditsLeft > 0;
    const featuringPublisherName = (featuringPublisher && featuringPublisher.get('name')) || 'Evvnt';
    let btnText = '';
    if (usesExistingPackage) {
      btnText = 'Submit Event';
    } else {
      if (packageType === 'free_publisher') {
        btnText = 'Preview Now, Upgrade Later';
      }
      else {
        btnText = this.isSelectedFree() ? 'Stay Free, Upgrade Later' : submitButtonText;
      }
    }
    let creditsRemainingLabel = 'credits remaining';
    let creditsRemainingNumber = creditsLeft;
    if (creditsLeft >= 200) {
      creditsRemainingLabel = 'credits';
      creditsRemainingNumber = 1;
    }

    const displayPrintEligible = updatePrintEligible !== undefined;
    const terms_url = ( this.props.publisher_id === null ? 'https://evvnt.com/terms/' : 'https://evvnt.com/partner-terms-and-conditions/');
    const price = currentPrice && currentPrice.toLocaleString();
    return (
      <Card className={styles.PackageSummary}>
        <div className="card-header card-header--with-action">
          <h4>Your order</h4>
          { publisher_id === null ? this.promoterPackageOptions() : this.partnerPackageOptions() }
        </div>
        <CardBody>
          {this.shouldDisplayPackages() && !usesExistingPackage && packageType !== 'single' && packageType !== 'free' && <div className="row no-gutters align-items-center mb-2">
            <div className="col-5">
              <NumberInput
                value={eventQuantity}
                onChange={onQuantityChange}
                min={packageType === 'subscription' ? 4 : 1}
                disabled={this.isSelectedFree()||isPrepay}
              />
            </div>
            <div className="col text-center">
              <div className={styles.promotedText}>
                Event{eventQuantity === 1 ? '' : 's'} Credit{eventQuantity === 1 ? '' : 's'}
              </div>
            </div>
          </div>}
          <div className="row justify-content-center no-gutters mb-2">
            <div className="col-5">
              {this.shouldDisplayPackages() && usesExistingPackage && <div className="info-box">
                <h2>{creditsRemainingNumber}</h2>
                <span>{creditsRemainingLabel}</span>
              </div>}
              {!usesExistingPackage && <div className="info-box">
                <h2 className="price-h2">
                  <CurrencySelect
                    currentCurrency={currency}
                    availableOptions={availableCurrencies}
                    onChange={this.handleCurrencyChange}
                  />
                  {isLoadingPrice ? <LoadingIndicator/> : price}
                  <div className="currentCurrency">{currency}</div>
                </h2>
                { packageType !== 'free' ? <span>+ applicable taxes</span> : '' }
                { packageType === 'subscription' && <span>per month</span> }
              </div>}
            </div>
            {this.shouldDisplayPackages() && <div className="col">
              <ul className={styles.list}>
                {isPrepay && packageType === 'subscription' &&
                  <li>Monthly Subscription</li>
                }
                {isPrepay && packageType === 'single' &&
                  <li>One-off purchase</li>
                }
                {this.currentPackagePerks().map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <ul className={[styles.list, styles.consList].join(' ')}>
                {this.currentPackageCons().map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>}
            {email_campaign_id !== null && <div className="col">
              <ul>
                <li className="planName">
                  Email Campaign
                </li>
              </ul>
              <ul className={styles.list}>
                {this.currentPackagePerks().map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <ul className={[styles.list, styles.consList].join(' ')}>
                {this.currentPackageCons().map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>}
            {publisher_id !== null && <div className="col">
              <ul>
                <li className="planName">
                  Enterprise Subscription
                </li>
              </ul>
              <ul className={styles.list}>
                {this.currentPackagePerks().map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <ul className={[styles.list, styles.consList].join(' ')}>
                {this.currentPackageCons().map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>}
          </div>
          <Button
            className="btn-labeled"
            block={true}
            disabled={isLoading}
            color="success"
            onClick={onSubmit}
          >
            {btnText}<span className="btn-label">{isLoading ? <LoadingIndicator/> : <img src={LockIcon}/>}</span>
          </Button>
          <small>
            By clicking this button, you agree to <strong>Evvnt's</strong> <a href="https://evvnt.com/privacy/" rel="noopener noreferrer" target="_blank">Privacy Policy</a> and <a href={terms_url} rel="noopener noreferrer" target="_blank">Terms of Use</a>. All of our transactions are <strong>secure</strong>.
            { displayPrintEligible &&
            <div className="form-group py-2">
              <input type="checkbox"
                id="print_eligible"
                style={{verticalAlign: 'middle', marginRight: '5px'}}
                checked={printEligible}
                onChange={updatePrintEligible}/>
              <span>Event listing will be sent to {featuringPublisherName} newsroom for consideration.</span>
            </div>
            }
          </small>
        </CardBody>
      </Card>
    );
  }
}



// WEBPACK FOOTER //
// app/client/bundles/EventSubmission/components/PackageSummary.jsx